import { useEffect, useRef, useState } from "react";
import { APIProvider, useApiIsLoaded } from "@vis.gl/react-google-maps";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

// Painel de Street View real (arrastável) — usado tanto no hero do app PWA
// (VehicleDetails em mobile-app.tsx) quanto na aba "Geral" do painel
// desktop (TabGeral em routes/index.tsx, que antes usava o hack legado
// output=svembed sem chave/API — trocado por pedir a mesma coisa nos dois
// lugares depois do bug relatado 2026-08-31 estar presente nos dois:
// "esse problema do Street View esta tanto no app e tanto web"). Fonte
// única pra nunca divergir entre as duas telas de novo (mesmo motivo do
// ALERT_TYPE_META em lib/alert-types.ts).
//
// source: GOOGLE (não OUTDOOR) é o que realmente resolve "ele ta ficando
// dentro de uma loja" — OUTDOOR ainda deixa passar fotos 360° enviadas por
// donos de estabelecimento fotografadas bem em frente à vitrine (tecnicamente
// "outdoor", mas visualmente parece "dentro do comércio"). GOOGLE restringe
// à coleção oficial capturada pelo carro da Street View — sempre no meio da
// rua, nunca uma contribuição de terceiro.
// Pool de widgets StreetViewPanorama (print 2026-10-05: mapa principal
// congelado mostrando tiles de zoom baixo esticados + Street View girando).
// Cada `new google.maps.StreetViewPanorama` abre um contexto WebGL próprio e
// o Google não tem API pra destruir — antes, cada abertura do painel de um
// veículo (e cada troca de aba Geral/Alertas/Sensores) criava um widget novo
// que nunca era liberado. O Chrome só mantém ~16 contextos WebGL vivos; ao
// estourar, derruba o mais antigo — normalmente o do mapa principal, que
// fica parado no último quadro. Agora os widgets são reaproveitados: ao
// desmontar, o host volta pra um "estacionamento" fora da tela e o próximo
// painel que montar pega ele de volta.
type PoolEntry = { host: HTMLDivElement; panorama: google.maps.StreetViewPanorama };
const freePanoramas: PoolEntry[] = [];
let parking: HTMLDivElement | null = null;

function acquirePanorama(): PoolEntry {
  const reused = freePanoramas.pop();
  if (reused) return reused;
  const host = document.createElement("div");
  host.style.width = "100%";
  host.style.height = "100%";
  const panorama = new google.maps.StreetViewPanorama(host, {
    addressControl: false,
    fullscreenControl: false,
    motionTracking: false,
    motionTrackingControl: false,
    visible: false,
  });
  return { host, panorama };
}

function releasePanorama(entry: PoolEntry) {
  entry.panorama.setVisible(false);
  if (!parking) {
    parking = document.createElement("div");
    parking.setAttribute("aria-hidden", "true");
    parking.style.cssText = "position:fixed;left:-10000px;top:0;width:320px;height:160px;overflow:hidden;pointer-events:none;";
    document.body.appendChild(parking);
  }
  parking.appendChild(entry.host);
  freePanoramas.push(entry);
}

export function StreetViewPanorama({
  lat,
  lng,
  heading,
  className,
  fallback,
}: {
  lat: number;
  lng: number;
  heading?: number;
  className?: string;
  fallback: React.ReactNode;
}) {
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined) ?? "";
  if (!apiKey) {
    return <div className={className}>{fallback}</div>;
  }
  return (
    <APIProvider apiKey={apiKey}>
      <StreetViewPanoramaInner lat={lat} lng={lng} heading={heading} className={className} fallback={fallback} />
    </APIProvider>
  );
}

function StreetViewPanoramaInner({
  lat,
  lng,
  heading,
  className,
  fallback,
}: {
  lat: number;
  lng: number;
  heading?: number;
  className?: string;
  fallback: React.ReactNode;
}) {
  const apiLoaded = useApiIsLoaded();
  const containerRef = useRef<HTMLDivElement>(null);
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const entryRef = useRef<PoolEntry | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  // Bug relatado 2026-09-09 "o street view fica tudo preto e depois de
  // segundos aparece certinho": o próprio widget StreetViewPanorama do
  // Google desenha um canvas preto internamente enquanto os tiles da
  // imagem 360° ainda não chegaram, mesmo depois de setVisible(true) —
  // não é um bug nosso, é o comportamento padrão do produto. Some ficou
  // mais visível depois do preload da API ([[gpsdrive_streetview_preload_focus_flicker_battery_2026-09-09]]):
  // como o script carrega mais rápido agora, o painel monta e chega nessa
  // fase preta mais cedo, ficando mais tempo "preto" na tela em vez de
  // escondido atrás do carregamento geral do script. Corrige mostrando o
  // MESMO fallback (nunca a tela preta crua do widget) até o evento
  // "status_changed" confirmar que o pano carregado tem imagem de verdade.
  const [imageReady, setImageReady] = useState(false);

  // Arredonda a ~11m (4 casas) — sem isso, cada poll de posição (15s)
  // reconsultaria o StreetViewService por causa de ruído de GPS de
  // centímetros.
  const roundedLat = Math.round(lat * 1e4) / 1e4;
  const roundedLng = Math.round(lng * 1e4) / 1e4;
  const roundedHeading = heading != null ? Math.round(heading) : undefined;

  useEffect(() => {
    if (!apiLoaded || !containerRef.current) return;
    let cancelled = false;
    setImageReady(false);
    // Cria o widget JÁ (escondido), em paralelo com a consulta abaixo —
    // antes ele só era construído depois da resposta do getPanorama, e a
    // inicialização dele somava ao tempo de espera (pedido 2026-10-01:
    // "demora muito pra aparecer").
    if (!panoramaRef.current) {
      const entry = acquirePanorama();
      containerRef.current.appendChild(entry.host);
      entryRef.current = entry;
      panoramaRef.current = entry.panorama;
    }
    const service = new google.maps.StreetViewService();
    service.getPanorama(
      {
        location: { lat: roundedLat, lng: roundedLng },
        radius: 75,
        source: google.maps.StreetViewSource.GOOGLE,
        preference: google.maps.StreetViewPreference.NEAREST,
      },
      (data, status) => {
        if (cancelled || !containerRef.current) return;
        if (status !== google.maps.StreetViewStatus.OK || !data?.location?.pano) {
          setUnavailable(true);
          return;
        }
        setUnavailable(false);
        const panorama = panoramaRef.current;
        if (!panorama) return;
        // "pano_changed" confirma que o panorama TROCOU de fato pro pano
        // pedido (o widget já resolveu a imagem, não só recebeu o id) —
        // sinal mais próximo disponível na API pública de "isso não vai
        // mais mostrar o canvas preto padrão". addListenerOnce evita
        // acumular listener a cada troca de veículo/posição.
        //
        // Print 2026-10-05 (spinner eterno no painel do veículo): com o carro
        // andando, lat/lng/heading arredondados mudam, mas o getPanorama
        // devolve o MESMO pano de antes — setPano com o id atual não dispara
        // "pano_changed", e o imageReady=false do início do efeito nunca
        // voltava pra true.
        if (panorama.getPano() === data.location.pano) {
          setImageReady(true);
        } else {
          google.maps.event.addListenerOnce(panorama, "pano_changed", () => {
            if (!cancelled) setImageReady(true);
          });
        }
        panorama.setPano(data.location.pano);
        panorama.setPov({ heading: roundedHeading ?? 0, pitch: 0 });
        panorama.setVisible(true);
        // Print real 2026-09-09: mesmo com "pano_changed" já disparado
        // (imageReady=true, fallback escondido), o widget continuava
        // mostrando um retângulo preto sólido (com o logo "Google" e os
        // links de atribuição visíveis por cima, confirmando que é o
        // widget de verdade, não um erro/fallback nosso). Causa clássica
        // documentada do próprio Google Maps: o painel foi criado num
        // container cujo tamanho ainda não tinha assentado (0 ou tamanho
        // errado no instante da construção — comum dentro de cards/abas
        // com layout ainda se ajustando), e o widget não recalcula
        // sozinho depois. `google.maps.event.trigger(panorama, "resize")`
        // força o recálculo; chamado tanto uma vez logo após setVisible
        // quanto sempre que o próprio container muda de tamanho de
        // verdade (ResizeObserver abaixo), cobrindo tanto o assentamento
        // inicial do layout quanto trocas de aba/orientação depois.
        requestAnimationFrame(() => {
          if (!cancelled) google.maps.event.trigger(panorama, "resize");
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [apiLoaded, roundedLat, roundedLng, roundedHeading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let wasEmpty = container.clientWidth === 0 || container.clientHeight === 0;
    const observer = new ResizeObserver(() => {
      const panorama = panoramaRef.current;
      if (!panorama) return;
      google.maps.event.trigger(panorama, "resize");
      const isEmptyNow = container.clientWidth === 0 || container.clientHeight === 0;
      // Bug relatado 2026-09-09 "voltei pro início e ficou tudo branco":
      // no PWA, o card do Início fica dentro de uma aba escondida com
      // display:none (troca de aba não desmonta, ver mobile-app.tsx) em vez
      // de sumir da tela por completo — então esse mesmo painorama (e seu
      // canvas WebGL) passa por um ciclo tamanho-real -> 0x0 -> tamanho-real
      // toda vez que o usuário sai e volta pro Início. Só disparar "resize"
      // (suficiente pro caso de layout ainda assentando, no efeito acima)
      // não é suficiente pra recompor os tiles depois de já ter passado por
      // 0x0 — o canvas fica preso num estado em branco. Alternar
      // setVisible(false)->true força o widget a redesenhar do zero.
      if (wasEmpty && !isEmptyNow) {
        panorama.setVisible(false);
        requestAnimationFrame(() => {
          panorama.setVisible(true);
          google.maps.event.trigger(panorama, "resize");
        });
      }
      wasEmpty = isEmptyNow;
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Devolve o widget pro pool ao desmontar (fechar painel/trocar de aba).
  useEffect(
    () => () => {
      const entry = entryRef.current;
      if (entry) releasePanorama(entry);
      entryRef.current = null;
      panoramaRef.current = null;
    },
    [],
  );

  return (
    // Fundo escuro no próprio container: entre o widget aparecer e os tiles
    // da foto 360° chegarem, o Google deixa o painel vazio — antes isso
    // aparecia como um bloco BRANCO (pedido 2026-10-01). Carregando ≠ "sem
    // imagem": o fallback ("Sem imagem de rua disponível aqui") só aparece
    // quando o Google confirma que não há Street View no local.
    <div className={cn("relative bg-slate-900", className)}>
      <div ref={containerRef} className="h-full w-full" />
      {!unavailable && !imageReady && (
        <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-slate-900 to-slate-700">
          <Loader2 className="h-6 w-6 animate-spin text-white/70" aria-label="Carregando imagem da rua" />
        </div>
      )}
      {unavailable && <div className="absolute inset-0">{fallback}</div>}
    </div>
  );
}
